/**
 * The bulk-upload modal's view, the only one: bulk upload is desktop-only by design, and
 * `BulkUploadModal.tsx` renders it at `lg` and up.
 * Shows the step components, the stepper, the submit button and the "Cancel the Bulk Upload"
 * confirmation. All state and calls live in `BulkUploadModal.tsx`.
 *
 * Closing (the X, or the button on the processing step) first asks for confirmation; only
 * "Cancel the Bulk Upload" closes the modal, which discards the upload.
 */
import React from "react";
import { Modal } from "@ryerson/frontend.modal";

import { Stepper } from "@ryerson/frontend.progress";
import { MapFields } from "./MapFields";
import { ContentSection, Flex } from "@ryerson/frontend.layout";
import { Button } from "@ryerson/frontend.button";
import { ProcessingFile } from "./ProcessingFile";
import { Typography } from "@ryerson/frontend.typography";
import { useTheme } from "@ryerson/frontend.theme";
import { UploadValidation } from "./UploadValidation";
import { BulkUploadModalType, BulkUploadType, FileMapping } from "./BulkUpload.model";
import { BulkUploadModalText } from "./BulkUploadModalContent";
import { FileSelection } from "./FileSelection";

/** Props of `MapFields.tsx`, the map-fields step. */
export type MapFieldsProps = {
	fileFields: string[];
	fileMapping: FileMapping[];
	isMissingRequiredData: boolean;
	handleDropdownSelection: (newValue: string, fileField: FileMapping) => void;
	isGenericError: boolean;
	saveMapping: boolean;
	setSaveMapping: React.Dispatch<React.SetStateAction<boolean>>;
	isLoggedIn: boolean;
};

/** Props of `FileSelection.tsx`, the file step. */
export type BulkUploadStepProps = {
	content: BulkUploadModalText;
	fileFields?: string[];
	bulkUploadFiles?: File[];
	setBulkUploadFiles?: (value: File[]) => void;
	submittedFileInput?: boolean;
	validFile?: boolean;
	bulkUploadType: BulkUploadType;
};

/** Renders the current step of the bulk upload; see the file header. */
const BulkUploadModalDesktop: React.FC<BulkUploadModalType> = ({
	content,
	showBulkUpload,
	setShowBulkUpload,
	bulkUploadStep,
	steps,
	fileMapping,
	isMissingRequiredData,
	onSubmit,
	disabledSubmitButton,
	handleDropdownSelection,
	submittedFileInput,
	bulkUploadFiles,
	setBulkUploadFiles,
	fileFields,
	buttonText,
	validFile,
	displayCancelUpload,
	setDisplayCancelUpload,
	validatedData,
	setValidatedData,
	processCorrections,
	setProcessCorrections,
	invalidData,
	setInvalidData,
	invalidDataRef,
	bulkUploadType,
	isGenericError,
	saveMapping,
	setSaveMapping,
	isLoggedIn,
}) => {
	const { theme } = useTheme();
	const { darkGray } = theme.colors.primary;

	const getModalButtons = () => {
		return displayCancelUpload ? (
			<Flex justifyContent="flex-start" css={{ width: "100%" }}>
				<Button
					disabled={false}
					css={{ width: "224px" }}
					size="lg"
					fullwidth
					label={content.continue}
					onClick={() => setDisplayCancelUpload(false)}
					dataTestId="bulk-upload-modal-continue-button"
				/>
				<Button
					disabled={false}
					css={{ marginLeft: "20px" }}
					size="lg"
					fullwidth
					background={darkGray}
					foreground="light"
					label={content.cancelBulkUpload}
					onClick={() => {
						setShowBulkUpload(false);
					}}
					dataTestId="bulk-upload-modal-cancel-button"
				/>
			</Flex>
		) : (
			<Flex justifyContent="flex-start" css={{ width: "100%" }}>
				<Button
					dataTestId="bulk-upload-submit-button"
					disabled={false}
					css={{ minWidth: "200px" }}
					size="lg"
					fullwidth
					label={buttonText}
					// The button is never visually disabled: while `disabledSubmitButton` is set
					// (no valid file on step 1, or rows already handed over on step 4) a click
					// does nothing. On step 3 it opens the cancel confirmation instead of
					// submitting.
					onClick={
						disabledSubmitButton
							? () => {}
							: bulkUploadStep === 3
							? () => setDisplayCancelUpload(true)
							: onSubmit
					}
				/>
			</Flex>
		);
	};

	const getModalContent = () => {
		return (
			<Flex css={{ width: 850 }} direction="column">
				{displayCancelUpload ? (
					<Flex css={{ padding: "20px 0 40px 0" }}>
						<Typography>{content.confirmCancel}</Typography>
					</Flex>
				) : (
					<>
						{bulkUploadStep !== 3 && <Stepper steps={steps} labelAlignment="center" />}
						{bulkUploadStep === 1 && (
							<FileSelection
								content={content}
								bulkUploadFiles={bulkUploadFiles}
								setBulkUploadFiles={setBulkUploadFiles}
								validFile={validFile}
								submittedFileInput={submittedFileInput}
								bulkUploadType={bulkUploadType}
							/>
						)}
						{bulkUploadStep === 2 && (
							<MapFields
								fileFields={fileFields}
								fileMapping={fileMapping}
								isMissingRequiredData={isMissingRequiredData}
								handleDropdownSelection={handleDropdownSelection}
								isGenericError={isGenericError}
								saveMapping={saveMapping}
								setSaveMapping={setSaveMapping}
								isLoggedIn={isLoggedIn}
							/>
						)}
						{bulkUploadStep === 3 && (
							<ProcessingFile content={content} onSubmit={onSubmit} />
						)}
						{bulkUploadStep === 4 && (
							<UploadValidation
								validatedData={validatedData}
								setValidatedData={setValidatedData}
								processCorrections={processCorrections}
								setProcessCorrections={setProcessCorrections}
								invalidData={invalidData}
								setInvalidData={setInvalidData}
								invalidDataRef={invalidDataRef}
								bulkUploadType={bulkUploadType}
							/>
						)}
					</>
				)}
			</Flex>
		);
	};

	return (
		<ContentSection vPadding="0" hPadding="0">
			<Modal
				title={
					bulkUploadStep === 3
						? " "
						: displayCancelUpload
						? content.cancelBulkUpload
						: content.BulkUpload
				}
				dataTestId="bulk-upload-modal"
				isOpen={showBulkUpload}
				bodyType="primary"
				type="primary"
				closable={bulkUploadStep === 3 || displayCancelUpload ? false : true}
				onClose={() => {
					setDisplayCancelUpload(!displayCancelUpload);
				}}
				buttonArea={getModalButtons()}
			>
				{getModalContent()}
			</Modal>
		</ContentSection>
	);
};

export default BulkUploadModalDesktop;
